import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Banknote, TrendingUp, Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';
import { businessToday } from '@/lib/format';

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

// Helper to extract date from transaction_at timestamp
const getDateFromTimestamp = (timestamp: string): string => {
    return timestamp.substring(0, 10);
};

export default function LoanRepaymentsAnalytics() {
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    const { data: summaryData, isLoading: summaryLoading } = useQuery({
        queryKey: ['loan-repayments-analytics-summary', fromDate, toDate],
        queryFn: async () => {
            const { data: repayments, error } = await supabase
                .from('loan_repayments')
                .select('id, transactions(amount, transaction_at)');
            if (error) throw error;

            const filtered = (repayments || []).filter(r => {
                const trans = r.transactions as { amount: number; transaction_at: string } | null;
                if (!trans?.transaction_at) return false;
                const transDate = getDateFromTimestamp(trans.transaction_at);
                return transDate >= fromDate && transDate <= toDate;
            });

            const totalCollected = filtered.reduce((sum, r) => {
                const trans = r.transactions as { amount: number; transaction_at: string } | null;
                return sum + (trans?.amount || 0);
            }, 0);
            const totalTransactions = filtered.length;
            const avgPayment = totalTransactions > 0 ? totalCollected / totalTransactions : 0;

            return { totalCollected, totalTransactions, avgPayment };
        },
    });

    const { data: monthlyData, isLoading: monthlyLoading } = useQuery({
        queryKey: ['loan-repayments-analytics-monthly', fromDate, toDate],
        queryFn: async () => {
            const months = eachMonthOfInterval({ start: monthRange.from, end: monthRange.to });
            const monthlyMap = new Map<string, number>();
            months.forEach(m => monthlyMap.set(format(m, 'yyyy-MM'), 0));

            const { data: repayments, error } = await supabase
                .from('loan_repayments')
                .select('transactions(amount, transaction_at)');
            if (error) throw error;

            (repayments || []).forEach(r => {
                const trans = r.transactions as { amount: number; transaction_at: string } | null;
                if (!trans?.transaction_at) return;
                const transDate = getDateFromTimestamp(trans.transaction_at);
                if (transDate < fromDate || transDate > toDate) return;
                const monthKey = transDate.substring(0, 7);
                if (monthlyMap.has(monthKey)) {
                    monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + (trans.amount || 0));
                }
            });

            return Array.from(monthlyMap.entries()).map(([month, amount]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
                amount,
            }));
        },
    });

    if (summaryLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
                <Skeleton className="h-80" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Collected</CardTitle>
                        <Banknote className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-green-600">{formatCurrency(summaryData?.totalCollected || 0)}</div>
                        <p className="text-xs text-muted-foreground">{summaryData?.totalTransactions || 0} payments</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Average Payment</CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{formatCurrency(summaryData?.avgPayment || 0)}</div>
                        <p className="text-xs text-muted-foreground">Per transaction</p>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Transactions</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{summaryData?.totalTransactions || 0}</div>
                        <p className="text-xs text-muted-foreground">In selected period</p>
                    </CardContent>
                </Card>
            </div>

            <Card className="border border-border/40">
                <CardHeader>
                    <CardTitle>Monthly Collection Trend</CardTitle>
                    <CardDescription>Advance repayments collected per month</CardDescription>
                </CardHeader>
                <CardContent>
                    {monthlyLoading ? (
                        <Skeleton className="h-64" />
                    ) : (
                        <ResponsiveContainer width="100%" height={280}>
                            <AreaChart data={monthlyData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="month" className="text-xs" />
                                <YAxis className="text-xs" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }} />
                                <Area type="monotone" dataKey="amount" stroke="hsl(var(--chart-2))" fill="hsl(var(--chart-2))" fillOpacity={0.6} name="Collected" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
